"use client";

import { forwardRef, useId } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface SelectProps extends React.ComponentProps<"select"> {
  label?: string;
  helperText?: string;
  error?: string;
  optional?: boolean;
  placeholder?: string;
  options: { value: string; label: string; disabled?: boolean }[];
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      label,
      helperText,
      error,
      optional,
      placeholder,
      options,
      id: externalId,
      ...props
    },
    ref
  ) => {
    const autoId = useId();
    const id = externalId || autoId;
    const helperId = `${id}-helper`;
    const errorId = `${id}-error`;

    const describedBy = error ? errorId : helperText ? helperId : undefined;

    const selectElement = (
      <div className="relative">
        <select
          ref={ref}
          id={id}
          data-slot="select"
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            [
              "flex h-9 w-full appearance-none",
              "rounded-[var(--radius-md)] border",
              "bg-[var(--color-bg-card)] pl-[var(--space-3)] pr-[var(--space-8)] py-[var(--space-2)]",
              "text-[length:var(--font-size-base)] md:text-[length:var(--font-size-sm)]",
              "text-[var(--color-text-primary)]",
              "transition-colors duration-[var(--duration-normal)]",
              "outline-none cursor-pointer",
              "border-[var(--color-border-default)]",
              "focus-visible:border-[var(--color-border-focus)]",
              "focus-visible:ring-[3px] focus-visible:ring-[var(--color-primary-muted)]",
              "aria-invalid:border-[var(--color-error)]",
              "aria-invalid:ring-[3px] aria-invalid:ring-[var(--color-error-muted)]",
              "disabled:pointer-events-none disabled:cursor-not-allowed",
              "disabled:bg-[var(--color-bg-muted)] disabled:opacity-50",
            ].join(" "),
            className
          )}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown
          className="pointer-events-none absolute right-[var(--space-2-5)] top-1/2 size-4 -translate-y-1/2 text-[var(--color-text-muted)]"
          aria-hidden="true"
        />
      </div>
    );

    if (!label && !helperText && !error) {
      return selectElement;
    }

    return (
      <div className="flex flex-col gap-[var(--space-1-5)]">
        {label && (
          <label
            htmlFor={id}
            className="text-[length:var(--font-size-sm)] font-[number:var(--font-weight-medium)] text-[var(--color-text-primary)]"
          >
            {label}
            {optional && (
              <span className="ml-1 text-[length:var(--font-size-xs)] font-[number:var(--font-weight-normal)] text-[var(--color-text-muted)]">
                (optional)
              </span>
            )}
          </label>
        )}
        {selectElement}
        {error ? (
          <p
            id={errorId}
            role="alert"
            className="text-[length:var(--font-size-xs)] text-[var(--color-error)]"
          >
            {error}
          </p>
        ) : helperText ? (
          <p
            id={helperId}
            className="text-[length:var(--font-size-xs)] text-[var(--color-text-muted)]"
          >
            {helperText}
          </p>
        ) : null}
      </div>
    );
  }
);
Select.displayName = "Select";

export { Select };
export type { SelectProps };
